'use client';

import React, { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Message {
  id: number;
  sender: string;
  message: string;
  timestamp: string;
  isInstructor: boolean;
  isPrivate?: boolean;
}

interface ChatPanelProps {
  messages: Message[];
}

const ChatPanel = ({ messages: initialMessages }: ChatPanelProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [newMessage, setNewMessage] = useState('');
  const [showPrivateChat, setShowPrivateChat] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (isHydrated) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isHydrated]);

  if (!isHydrated) {
    return (
      <div className="h-full bg-card rounded-lg border border-border p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-muted rounded w-3/4"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
          <div className="h-4 bg-muted rounded w-5/6"></div>
        </div>
      </div>
    );
  }

  const handleSendMessage = () => {
    if (newMessage.trim()) {
      const message: Message = {
        id: messages.length + 1,
        sender: 'You',
        message: newMessage,
        timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        isInstructor: false,
        isPrivate: showPrivateChat
      };
      setMessages([...messages, message]);
      setNewMessage('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="h-full flex flex-col bg-card rounded-lg border border-border">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="ChatBubbleLeftRightIcon" size={20} variant="outline" className="text-primary" />
          <h3 className="font-headline font-semibold text-foreground">
            {showPrivateChat ? 'Private Chat' : 'Class Chat'}
          </h3>
        </div>
        <button
          onClick={() => setShowPrivateChat(!showPrivateChat)}
          className="px-3 py-1 text-sm font-medium text-primary hover:bg-muted rounded-md transition-colors"
        >
          {showPrivateChat ? 'Public' : 'Private'}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages
          .filter(msg => !showPrivateChat || msg.isPrivate)
          .map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.sender === 'You' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[80%] ${msg.sender === 'You' ? 'order-2' : 'order-1'}`}>
                <div className="flex items-center space-x-2 mb-1">
                  <span className={`text-xs font-medium ${msg.isInstructor ? 'text-primary' : 'text-muted-foreground'}`}>
                    {msg.sender}
                    {msg.isInstructor && (
                      <span className="ml-1 px-1.5 py-0.5 bg-primary text-white text-[10px] rounded">
                        Instructor
                      </span>
                    )}
                  </span>
                  <span className="text-xs text-muted-foreground">{msg.timestamp}</span>
                </div>
                <div
                  className={`px-4 py-2 rounded-lg ${
                    msg.sender === 'You' ?'bg-primary text-white'
                      : msg.isInstructor
                      ? 'bg-brand-secondary text-foreground'
                      : 'bg-muted text-foreground'
                  }`}
                >
                  <p className="text-sm">{msg.message}</p>
                </div>
              </div>
            </div>
          ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="p-4 border-t border-border">
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={showPrivateChat ? 'Send private message to instructor...' : 'Type your message...'}
            className="flex-1 px-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground bg-background"
          />
          <button
            onClick={handleSendMessage}
            disabled={!newMessage.trim()}
            className="p-2 bg-primary text-white rounded-md hover:bg-opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Send message"
          >
            <Icon name="PaperAirplaneIcon" size={20} variant="solid" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;